/**
 * External Imports
 */
import { Dispatch, FC, SetStateAction, useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

/**
 * Internal Imports
 */
import CNEdListTable from '../../../components/customTables/support/cnedListTable';
import SearchHeader from '../../../components/searchHeader';
import SupportSideNav from '../../../components/sideNav/SupportSideNav';

import { RootState } from '../../../redux/store';
import { getCNEdSubjectsToRoundForList } from '../../../redux/actions';

import { date } from '../../../utils/helpers';
/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Employee,
  RoundHistory
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { SupportContainer } from '../styles/Support.styles';

export interface CNEdListProps {
  authorizedUser?: AuthUserProps;
  cnedSubjectsToRound: Array<Employee & RoundHistory>;
  selectedDeptsFromStore: string[];
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const CNEdList: FC<CNEdListProps> = ({
  authorizedUser,
  cnedSubjectsToRound = [],
  selectedDeptsFromStore,
  searchTerm,
  setSearchTerm
}) => {
  const dispatch = useDispatch();

  const { cnedFilter = 'all' } = useParams();

  useEffect(() => {
    if (authorizedUser?.hcaid)
      dispatch(
        getCNEdSubjectsToRoundForList({
          hcaid: authorizedUser.hcaid
        })
      );
  }, []);

  const filteredCNEdRounds = useMemo(() => {
    const filteredByDept = cnedSubjectsToRound.filter(employee => {
      const employeeDept =
        (employee.adHoc ? employee?.employeeDept : employee?.dept) || 'Unknown';
      return selectedDeptsFromStore.some(
        selectedDept => selectedDept === employeeDept
      );
    });

    return filteredByDept.reduce(
      (
        acc: {
          cnedSubjects: (Employee & RoundHistory)[];
          adHoc: (Employee & RoundHistory)[];
        },
        employee: Employee & RoundHistory
      ) =>
        employee.adHoc
          ? { ...acc, adHoc: [...acc.adHoc, employee] }
          : { ...acc, cnedSubjects: [...acc.cnedSubjects, employee] },
      { cnedSubjects: [], adHoc: [] }
    );
  }, [cnedFilter, cnedSubjectsToRound, selectedDeptsFromStore]);

  const noRoundCount = useMemo(() => {
    return filteredCNEdRounds.cnedSubjects.filter(
      employee =>
        !employee?.lastRoundDate ||
        (employee?.lastRoundDate &&
          date.isOverInDays(employee.lastRoundDate, 30))
    ).length;
  }, [filteredCNEdRounds, cnedSubjectsToRound]);
  const recentlyCompletedCount = useMemo(() => {
    return (
      filteredCNEdRounds.cnedSubjects.filter(
        employee =>
          employee?.lastRoundDate &&
          !date.isOverInWeeks(employee.lastRoundDate, 2)
      ).length +
      filteredCNEdRounds.adHoc.filter(
        employee =>
          employee?.lastRoundDate &&
          !date.isOverInWeeks(employee.lastRoundDate, 2)
      ).length
    );
  }, [filteredCNEdRounds, cnedSubjectsToRound]);
  const newNursesCount = useMemo(() => {
    return filteredCNEdRounds.cnedSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewNurse'
      )
    ).length;
  }, [filteredCNEdRounds, cnedSubjectsToRound]);
  const newToHCACount = useMemo(() => {
    return filteredCNEdRounds.cnedSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewToHCA'
      )
    ).length;
  }, [filteredCNEdRounds, cnedSubjectsToRound]);
  const newToDeptCount = useMemo(() => {
    return filteredCNEdRounds.cnedSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewToDepartment'
      )
    ).length;
  }, [filteredCNEdRounds, cnedSubjectsToRound]);

  return (
    <SupportContainer id="Support-Container">
      <SupportSideNav
        type="cned"
        total={noRoundCount + recentlyCompletedCount}
        noRoundCount={noRoundCount}
        recentlyCompletedCount={recentlyCompletedCount}
        newNursesCount={newNursesCount}
        newToHCACount={newToHCACount}
        newToDeptCount={newToDeptCount}
      />
      <div className="d-flex flex-column w-100">
        <SearchHeader searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        <CNEdListTable
          cnedFilter={cnedFilter}
          searchTerm={searchTerm}
          cnedRounds={filteredCNEdRounds.cnedSubjects}
          adhocRounds={filteredCNEdRounds.adHoc}
        />
      </div>
    </SupportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    cnedSubjectsToRound: state.EmployeeReducer.cnedSubjectsToRoundForList,
    selectedDeptsFromStore: state.EmployeeReducer.selectedDepts
  };
};

export default connect(mapReduxStateToProps)(CNEdList);
